import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Container,
  FormControl,
  Grid,
  TextFieldProps,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../hooks';
import { TPTextField } from '../../../components/Inputs';
import { getAuthState } from '../../../store/AuthSlice';
import { getCourseStudentsByUser } from '../../../store/V2/CourseStudentsSlice/api';
import {
  getCourseStudentsState,
  resetCourseStudents,
} from '../../../store/V2/CourseStudentsSlice';
import {
  getSubmissionsGroupedByStudents,
  getSubmissionsByCourse,
  getSubmissionsByStudent,
} from '../../../store/V2/SubmissionsSlice/api';
import {
  getSubmissionsV2State,
  resetCourseSubmissionsData,
  resetGroupedStudentsSubmissions,
  resetStudentSubmissionsData,
} from '../../../store/V2/SubmissionsSlice';
import NoData from '../../../components/V2/Empty/NoData';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import StudentPerformance from '../QuizPerformance/StudentPerformance';
import DetailedOverview from '../QuizPerformance/DetailedOverview';

type TSelectOption = {
  label: string;
  value: string;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
      fontSize: '14px',
      borderRadius: '10px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

type TState = {
  selectedCourse: TSelectOption | null;
};

const initialState: TState = {
  selectedCourse: null,
};

const MyPerformance: React.FC = () => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState(initialState);
  const [coursesOptions, setCoursesOptions] = useState<TSelectOption[]>([]);

  // Store
  const { user: authUser } = useAppSelector(getAuthState);
  const { courseStudents } = useAppSelector(getCourseStudentsState);
  const { courseSubmissionsData, status } = useAppSelector(
    getSubmissionsV2State
  );

  useEffect(() => {
    if (authUser) {
      dispatch(getCourseStudentsByUser(authUser._id));
    }

    return () => {
      dispatch(resetCourseStudents());
      dispatch(resetCourseSubmissionsData());
      dispatch(resetStudentSubmissionsData());
      dispatch(resetGroupedStudentsSubmissions());
    };
  }, []);

  useEffect(() => {
    if (state.selectedCourse && authUser) {
      dispatch(getSubmissionsByCourse(state.selectedCourse.value));
      dispatch(getSubmissionsGroupedByStudents(state.selectedCourse.value));
      dispatch(
        getSubmissionsByStudent({
          courseId: state.selectedCourse.value,
          studentId: authUser._id,
        })
      );
    }
  }, [state.selectedCourse]);

  useEffect(() => {
    const options = courseStudents.map((courseStudent) => ({
      label: courseStudent.course.title,
      value: courseStudent.course._id,
    }));

    updateStateAttributes({
      selectedCourse: options[0],
    });

    setCoursesOptions(options);
  }, [courseStudents]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => {
      const newState = {
        ...prevState,
        ...attributes,
      };

      return newState;
    });
  };

  const handleCourseChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    updateStateAttributes({
      selectedCourse: value,
    });

    dispatch(resetCourseSubmissionsData());
  };

  return (
    <Container maxWidth="lg">
      <Grid container spacing={4} paddingTop="34px">
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.selectedCourse}
                  options={coursesOptions}
                  onChange={handleCourseChange}
                  popupIcon={<KeyboardArrowDownIcon fontSize="small" />}
                  getOptionKey={(option) => option.value}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      placeholder="Course"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>

          {!courseSubmissionsData && status === 'loading' ? (
            <BasicLoader
              containerProps={{
                minHeight: '300px',
                alignItems: 'center',
              }}
            />
          ) : null}

          {courseSubmissionsData?.groupedSubmissions.length === 0 ||
          status === 'rejected' ? (
            <NoData title="No submissions found" />
          ) : null}

          {courseSubmissionsData?.groupedSubmissions.length ? (
            <>
              <StudentPerformance />

              {authUser && (
                <DetailedOverview
                  selectedStudent={{
                    label: authUser.firstname,
                    value: authUser._id,
                  }}
                />
              )}
            </>
          ) : null}
        </Grid>
      </Grid>
    </Container>
  );
};

export default MyPerformance;
